import { isTimeInLinkRange } from '@src/stores-v2/SceneLayoutIndexStore';

import type { ElementState } from '@src/renderer/ElementState';
import type SceneLayoutIndexStore from '@src/stores-v2/SceneLayoutIndexStore';

/**
 * SceneLayoutSelectionManager delegates visible representation resolution to SceneLayoutIndexStore,
 * keeping selection logic independent from renderer-specific heuristics.
 */
class SceneLayoutSelectionManager {
  private readonly layoutIndexStoreProvider: () =>
    | SceneLayoutIndexStore
    | undefined;

  private readonly elementStateResolver: (
    id: string,
  ) => ElementState | undefined;

  private readonly activeElementIdsProvider: () => readonly string[];

  private readonly currentTimeProvider: () => number | undefined;

  constructor(
    layoutIndexStoreProvider: () => SceneLayoutIndexStore | undefined,
    elementStateResolver: (id: string) => ElementState | undefined,
    activeElementIdsProvider: () => readonly string[],
    currentTimeProvider: () => number | undefined,
  ) {
    this.layoutIndexStoreProvider = layoutIndexStoreProvider;
    this.elementStateResolver = elementStateResolver;
    this.activeElementIdsProvider = activeElementIdsProvider;
    this.currentTimeProvider = currentTimeProvider;
  }

  resolveElementForActivation(element: ElementState): ElementState {
    const elementId = element.source?.id;
    if (typeof elementId !== 'string') {
      return element;
    }

    const layoutIndexStore = this.layoutIndexStoreProvider();
    if (!layoutIndexStore) {
      return element;
    }

    const targetId = layoutIndexStore.resolveVisibleRepresentationId(
      elementId,
      this.getReferenceTime(element),
    );

    if (!targetId || targetId === elementId) {
      return element;
    }

    const resolved = this.elementStateResolver(targetId);
    return resolved ?? element;
  }

  /**
   * Resolve an element to its underlying (parent) element.
   *
   * `seek` defaults to the source-only `elementStateResolver` (which throws on
   * a miss). Callers resolving to an editor-side parent — whose raw id isn't in
   * the windowed renderer source — should pass an editor-aware seeker so the
   * lookup doesn't throw on a windowed-out parent.
   */
  resolveElementToParent(
    element: ElementState,
    seek: (id: string) => ElementState | undefined = this.elementStateResolver,
  ): ElementState {
    const elementId = element.source?.id;
    if (typeof elementId !== 'string') {
      return element;
    }

    const layoutIndexStore = this.layoutIndexStoreProvider();
    if (!layoutIndexStore) {
      return element;
    }

    const parentId = layoutIndexStore.resolveParentId(elementId);
    if (!parentId || parentId === elementId) {
      return element;
    }

    const resolved = seek(parentId);
    return resolved ?? element;
  }

  resolveElementToComposition(element: ElementState): ElementState {
    const elementId = element.source?.id;
    if (typeof elementId !== 'string') {
      return element;
    }

    const layoutIndexStore = this.layoutIndexStoreProvider();
    if (!layoutIndexStore) {
      return element;
    }

    const compositionId = layoutIndexStore.resolveCompositionId(elementId);
    if (!compositionId || compositionId === elementId) {
      return element;
    }

    const resolved = this.elementStateResolver(compositionId);
    return resolved ?? element;
  }

  reselectInnerElement(
    activeElement: ElementState,
    onReselect: (elementId: string) => void,
  ): boolean {
    const resolved = this.resolveElementForActivation(activeElement);
    if (resolved.source.id !== activeElement.source.id) {
      onReselect(resolved.source.id);
      return true;
    }
    return false;
  }

  isLayoutElementActive(element: ElementState): boolean {
    const layoutIndexStore = this.layoutIndexStoreProvider();
    if (!layoutIndexStore) {
      return false;
    }

    const elementId = element.source?.id;
    if (typeof elementId !== 'string') {
      return false;
    }

    const records = layoutIndexStore.getRecords(elementId);
    if (!records || records.length === 0) {
      return false;
    }

    const activeIds = this.activeElementIdsProvider();
    const activeIdSet = new Set(activeIds.filter(id => typeof id === 'string'));

    // Direct match: elementId itself is active
    if (activeIdSet.has(elementId)) {
      return true;
    }

    // Check each record for related active elements
    const currentTime = this.currentTimeProvider();

    for (const record of records) {
      const { parentId, representationId, compositionId } = record.link;

      // parentId or compositionId match - always considered active
      if (typeof parentId === 'string' && activeIdSet.has(parentId)) {
        return true;
      }
      if (typeof compositionId === 'string' && activeIdSet.has(compositionId)) {
        return true;
      }

      // representationId match - only if this record's time range matches current time
      // This prevents false positives when multiple parents share a merged representationId
      if (
        typeof representationId === 'string' &&
        activeIdSet.has(representationId) &&
        parentId === elementId
      ) {
        // If no time info available, fall back to considering it active
        if (typeof currentTime !== 'number' || !Number.isFinite(currentTime)) {
          return true;
        }

        // Check if current time falls within this record's composition time range
        if (isTimeInLinkRange(record.link, currentTime)) {
          return true;
        }
        // Time doesn't match this record - continue checking other records
      }
    }

    return false;
  }

  private getReferenceTime(element: ElementState): number | undefined {
    const currentTime = this.currentTimeProvider();
    if (typeof currentTime === 'number' && Number.isFinite(currentTime)) {
      return currentTime;
    }

    if (
      typeof element.globalTime === 'number' &&
      Number.isFinite(element.globalTime)
    ) {
      return element.globalTime;
    }

    const sourceTime = element.source?.time;
    if (typeof sourceTime === 'number' && Number.isFinite(sourceTime)) {
      return sourceTime;
    }

    if (typeof sourceTime === 'string') {
      const parsed = Number(sourceTime);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return undefined;
  }
}

export default SceneLayoutSelectionManager;
